import React, { useState, useMemo, useEffect } from "react";
import { Plus, Search, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Agent,
  CreateAgentRequest,
  UpdateAgentRequest,
} from "../api/services/agents/type";
import { useAgentManagement } from "../hooks/useAgents";
import { AgentForm } from "../components/agents/AgentForm";
import { AgentTable } from "../components/agents/AgentTable";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { Pagination } from "../components/common/Pagination";
import toast from "react-hot-toast";

export const Agents: React.FC = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | undefined>(
    undefined,
  );
  const itemsPerPage = 25;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const queryParams = useMemo(
    () => ({
      skip: (currentPage - 1) * itemsPerPage,
      limit: itemsPerPage,
      name: debouncedSearchTerm || undefined,
    }),
    [currentPage, debouncedSearchTerm],
  );

  const {
    agents,
    isLoading,
    isError,
    error,
    createAgent,
    updateAgent,
    deleteAgent,
    isCreating,
    isUpdating,
    refreshAgents,
  } = useAgentManagement(queryParams);

  const totalPages = useMemo(() => {
    if (!agents?.pagination?.total) return 1;
    return Math.ceil(agents.pagination.total / itemsPerPage);
  }, [agents?.pagination?.total]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleCreateAgent = () => {
    setEditingAgent(undefined);
    setShowForm(true);
  };

  const handleEditAgent = (agent: Agent) => {
    setEditingAgent(agent);
    setShowForm(true);
  };

  const handleViewAgent = (agent: Agent) => {
    navigate(`/agents/${agent.id}`);
  };

  const handleFormSubmit = (data: CreateAgentRequest | UpdateAgentRequest) => {
    if (editingAgent) {
      updateAgent(
        { agentId: editingAgent.id, data },
        {
          onSuccess: () => {
            toast.success("Agent updated successfully!");
            setShowForm(false);
            setEditingAgent(undefined);
          },
          onError: (error) => {
            toast.error(`Failed to update agent: ${error.message}`);
          },
        },
      );
    } else {
      createAgent(data as CreateAgentRequest, {
        onSuccess: () => {
          toast.success("Agent created successfully!");
          setShowForm(false);
        },
        onError: (error) => {
          toast.error(`Failed to create agent: ${error.message}`);
        },
      });
    }
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState<string | null>(null);

  const handleDeleteAgent = (agentId: string) => {
    setAgentToDelete(agentId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (agentToDelete) {
      deleteAgent(agentToDelete, {
        onSuccess: () => {
          toast.success("Agent deleted successfully!");
          setDeleteDialogOpen(false);
          setAgentToDelete(null);
        },
        onError: (error) => {
          toast.error(`Failed to delete agent: ${error.message}`);
        },
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setAgentToDelete(null);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingAgent(undefined);
  };

  if (isError) {
    return (
      <div className="min-h-screen bg-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-800/20 border border-red-600 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-red-400 mb-2">
              Error Loading Agents
            </h3>
            <p className="text-red-300">
              {error?.message || "Failed to load agents. Please try again."}
            </p>
            <button
              onClick={() => refreshAgents()}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Search and Add Button */}
        <div className="bg-slate-800 rounded-lg p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Search agents by name..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={handleCreateAgent}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors font-medium whitespace-nowrap"
            >
              <Plus size={20} />
              Add Agent
            </button>
          </div>
        </div>

        {/* Agents Table */}
        <div className="mb-6 max-h-[calc(100vh-15rem)] overflow-auto rounded-lg">
          <AgentTable
            agents={agents?.items || []}
            onEdit={handleEditAgent}
            onDelete={handleDeleteAgent}
            onView={handleViewAgent}
            isLoading={isLoading}
          />
        </div>

        {/* Pagination */}
        {agents?.items && agents.items.length > 0 && totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        {/* Stats */}
        {agents?.pagination && (
          <div className="mt-6 text-sm text-slate-400 text-center">
            Showing {agents.pagination.returned} of {agents.pagination.total}{" "}
            agents
            {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
          </div>
        )}

        {/* Agent Form Modal */}
        {showForm && (
          <AgentForm
            agent={editingAgent}
            isLoading={isCreating || isUpdating}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialogOpen}
          title="Delete Agent"
          description="Are you sure you want to delete this agent? This action cannot be undone."
          icon={<Trash2 className="h-8 w-8 text-white" />}
          iconBgColor="bg-red-600"
          confirmColor="bg-red-600 hover:bg-red-700"
          confirmText="Delete"
          cancelText="Cancel"
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        />
      </div>
    </div>
  );
};
